import { useQuery } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getPrivateAxiosInstance } from '../axiosInstances/axiosPrivate';
import useAssociateActions from '../hooks/APIHooks/users/useAssociateActions';
import useRolePermissionsActions from '../hooks/APIHooks/users/useRolePermissionsActions';
import { AcademiaResponse, DecodedToken } from '../types/common';
import { FeaturePermissionData } from '../types/permissions';
import { Client, User } from '../types/users';
import { returnQueryConfig } from '../utils/commonFunctions';
import { getAccessToken } from '../utils/cookieHandler';

// datatype of the context provider declared here
interface IAuthProvider {
  currentUser: User | Client | undefined;
  currentUserType: string;
  currentUserPermissions: Array<FeaturePermissionData>;
  isFirstLogin: boolean;
  isUserApproved: boolean;
  setCurrentUserId: React.Dispatch<React.SetStateAction<number>>;
  setCurrentUserType: React.Dispatch<React.SetStateAction<string>>;
  setIsFirstLogin: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUserApproved: React.Dispatch<React.SetStateAction<boolean>>;
}

// creating the context here
const AuthContext = createContext<IAuthProvider | null>(null);

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  // integration of react hooks here
  const [currentUserId, setCurrentUserId] = useState<number>(0);
  const [currentUserType, setCurrentUserType] = useState<string>('');

  const [isFirstLogin, setIsFirstLogin] = useState<boolean>(false);
  const [isUserApproved, setIsUserApproved] = useState<boolean>(false);

  const [currentUserPermissions, setCurrentUserPermissions] = useState<
    Array<FeaturePermissionData>
  >([]);

  // fetching the user specific details from access token here
  useEffect(() => {
    const accessToken = getAccessToken();

    if (accessToken) {
      const decodedToken = jwtDecode<DecodedToken>(accessToken);

      const loggedInUserId = decodedToken.user_id;
      const loggedInUserType = decodedToken.user_type;
      const isFirstLoginStore = decodedToken.is_first_login;

      console.log('LOGGED IN USER TYPE: ', loggedInUserType);

      setCurrentUserId(+loggedInUserId);
      setCurrentUserType(loggedInUserType);
      setIsFirstLogin(isFirstLoginStore);
    }
  }, []);

  // fetching current user data here
  const { data: currentUser } = useQuery({
    queryKey: ['current-user-details', currentUserId],
    queryFn: async (): Promise<AcademiaResponse<User>> => {
      const response = await getPrivateAxiosInstance('user').get(
        `/user/${currentUserId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(!!currentUserId && currentUserType === 'user'),
  });

  // fetching current client data here
  const { data: currentClient } = useQuery({
    queryKey: ['current-client-details', currentUserId],
    queryFn: async (): Promise<AcademiaResponse<Client>> => {
      const response = await getPrivateAxiosInstance('user').get(
        `/client/${currentUserId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(
      !!currentUserId &&
        currentUserType === 'client' &&
        !window.location.pathname.includes('application')
    ),
  });

  // integration of custom API hooks here
  const { getUserFeaturePermissionsById } = useAssociateActions(
    'userFeaturePermissions',
    currentUserType === 'user' ? currentUserId : undefined
  );

  const { getRolePermissionsByRoleIdHandler } = useRolePermissionsActions(
    '',
    currentUser?.is_success && currentUserType === 'user'
      ? currentUser.data.role_id
      : undefined
  );

  // merging role wise permissions and user specific permissions here
  useMemo(() => {
    if (getRolePermissionsByRoleIdHandler.data?.is_success) {
      const roleWisePermissions =
        getRolePermissionsByRoleIdHandler.data.data.role_permissions.filter(
          (rolePermission) => rolePermission.permission_names.length
        );

      const userSpecificPermissions =
        getUserFeaturePermissionsById.data?.data.feature_permission_data || [];

      setCurrentUserPermissions(
        roleWisePermissions.concat(userSpecificPermissions)
      );
    } else {
      setCurrentUserPermissions([]);
    }
  }, [
    getRolePermissionsByRoleIdHandler.data,
    getUserFeaturePermissionsById.data,
  ]);

  console.log(
    'CURRENT USER: ',
    currentUser?.data,
    currentUserId,
    currentUserType
  );

  console.log(
    'CURRENT CLIENT: ',
    currentClient?.data,
    currentUserId,
    currentUserType
  );

  console.log('CURRENT USER CONFIGS: ', currentUserPermissions);

  // rendering the context provider here
  return (
    <AuthContext.Provider
      value={{
        currentUser:
          currentUserType === 'user' ? currentUser?.data : currentClient?.data,
        currentUserPermissions,
        setCurrentUserId,
        isFirstLogin,
        isUserApproved,
        currentUserType,
        setCurrentUserType,
        setIsFirstLogin,
        setIsUserApproved,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

// custom hook to access auth context declared here
export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be within AuthProvider!');
  }

  return context;
};
